export type MarkmapNode = {
  content?: string;
  children?: MarkmapNode[];
};

export type ExportPngOptions = {
  scale?: number;
  backgroundColor?: string;
};

type SerializeSvgOptions = {
  stripForeignObjects?: boolean;
};

function createId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `id-${Math.random().toString(16).slice(2)}-${Date.now()}`;
}

function escapeXml(input: string): string {
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function stripHtml(input: string): string {
  if (input.trim() === '') return '';

  try {
    if (typeof DOMParser === 'undefined') {
      return input.replace(/<[^>]*>/g, '').trim();
    }
    const doc = new DOMParser().parseFromString(input, 'text/html');
    return (doc.body.textContent ?? '').trim();
  } catch {
    return input.replace(/<[^>]*>/g, '').trim();
  }
}

function normalizeTopicTitle(title: string): string {
  const normalized = title.replace(/\s+/g, ' ').trim();
  return normalized;
}

function getNodeTitle(node: MarkmapNode): string {
  const title = stripHtml(node.content ?? '');
  return normalizeTopicTitle(title);
}

function buildTopicXml(node: MarkmapNode): string {
  const title = getNodeTitle(node) || 'Topic';
  const children = node.children ?? [];
  const childrenXml = children.length
    ? `<children><topics type="attached">${children.map(buildTopicXml).join('')}</topics></children>`
    : '';

  return `<topic id="${createId()}"><title>${escapeXml(title)}</title>${childrenXml}</topic>`;
}

export async function buildXmindBlobFromMarkmapRoot(
  root: MarkmapNode,
  opts?: { sheetTitle?: string; structureClass?: string }
): Promise<Blob> {
  const bytes = await buildXmindZipBytesFromMarkmapRoot(root, opts);
  const buffer = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength);
  return new Blob([buffer], { type: 'application/vnd.xmind.workbook' });
}

export async function buildXmindZipBytesFromMarkmapRoot(
  root: MarkmapNode,
  opts?: { sheetTitle?: string; structureClass?: string }
): Promise<Uint8Array> {
  const sheetTitle = opts?.sheetTitle ?? 'Mind Map';
  const structureClass = opts?.structureClass ?? 'org.xmind.ui.logic.right';
  const rootTitle = getNodeTitle(root) || sheetTitle;
  const rootChildren = root.children ?? [];

  const timestamp = new Date().toISOString();
  const sheetId = createId();
  const rootTopicId = createId();
  const rootChildrenXml = rootChildren.length
    ? `<children><topics type="attached">${rootChildren.map(buildTopicXml).join('')}</topics></children>`
    : '';

  const contentXml = `<?xml version="1.0" encoding="UTF-8" standalone="no"?>
<xmap-content xmlns="urn:xmind:xmap:xmlns:content:2.0" xmlns:xhtml="http://www.w3.org/1999/xhtml" timestamp="${escapeXml(timestamp)}">
  <sheet id="${escapeXml(sheetId)}">
    <title>${escapeXml(sheetTitle)}</title>
    <topic id="${escapeXml(rootTopicId)}" structure-class="${escapeXml(structureClass)}">
      <title>${escapeXml(rootTitle)}</title>
      ${rootChildrenXml}
    </topic>
  </sheet>
</xmap-content>
`;

  const manifestXml = `<?xml version="1.0" encoding="UTF-8" standalone="no"?>
<manifest xmlns="urn:xmind:xmap:xmlns:manifest:1.0">
  <file-entry full-path="content.xml" media-type="text/xml"/>
  <file-entry full-path="META-INF/" media-type=""/>
  <file-entry full-path="META-INF/manifest.xml" media-type="text/xml"/>
</manifest>
`;

  const { default: JSZip } = await import('jszip');
  const zip = new JSZip();
  zip.file('content.xml', contentXml);
  zip.folder('META-INF')?.file('manifest.xml', manifestXml);
  return zip.generateAsync({ type: 'uint8array', compression: 'DEFLATE' });
}

function getSvgExportDimensions(svg: SVGSVGElement): { width: number; height: number } {
  const rect = svg.getBoundingClientRect();
  const width = Math.max(1, Math.floor(rect.width));
  const height = Math.max(1, Math.floor(rect.height));

  if (width > 1 && height > 1) return { width, height };

  const widthAttr = Number(svg.getAttribute('width'));
  const heightAttr = Number(svg.getAttribute('height'));
  if (Number.isFinite(widthAttr) && widthAttr > 0 && Number.isFinite(heightAttr) && heightAttr > 0) {
    return { width: Math.floor(widthAttr), height: Math.floor(heightAttr) };
  }

  const viewBox = svg.viewBox?.baseVal;
  if (viewBox && viewBox.width > 0 && viewBox.height > 0) {
    return { width: Math.floor(viewBox.width), height: Math.floor(viewBox.height) };
  }

  return { width: 800, height: 600 };
}

function convertForeignObjectsToText(svg: SVGSVGElement): void {
  const foreignObjects = Array.from(svg.querySelectorAll('foreignObject'));
  if (foreignObjects.length === 0) return;

  const svgNs = 'http://www.w3.org/2000/svg';
  const measureCanvas = document.createElement('canvas');
  const measureCtx = measureCanvas.getContext?.('2d') ?? null;
  if (measureCtx) {
    measureCtx.font = '300 16px sans-serif';
  }

  const lineHeight = 20;

  for (const fo of foreignObjects) {
    const parent = fo.parentNode;
    if (!parent) continue;

    const x = Number(fo.getAttribute('x') ?? 0);
    const y = Number(fo.getAttribute('y') ?? 0);
    const width = Number(fo.getAttribute('width') ?? 0);
    const height = Number(fo.getAttribute('height') ?? 0);
    const textContent = (fo.textContent ?? '').replace(/\s+/g, ' ').trim();

    if (!textContent) {
      parent.removeChild(fo);
      continue;
    }

    const textEl = svg.ownerDocument.createElementNS(svgNs, 'text');
    const textX = x + 2;
    const textY = y + 16;
    textEl.setAttribute('x', String(textX));
    textEl.setAttribute('y', String(textY));
    textEl.setAttribute('fill', '#111827');
    textEl.setAttribute('font-size', '16');
    textEl.setAttribute('font-family', 'sans-serif');
    textEl.setAttribute('font-weight', '300');

    if (measureCtx && Number.isFinite(width) && width > 0) {
      const words = textContent.split(' ');
      const lines: string[] = [];
      let current = '';
      for (const word of words) {
        const candidate = current ? `${current} ${word}` : word;
        const candidateWidth = measureCtx.measureText(candidate).width;
        if (candidateWidth <= width || current === '') {
          current = candidate;
          continue;
        }
        lines.push(current);
        current = word;
      }
      if (current) lines.push(current);

      const maxLines = Number.isFinite(height) && height > 0 ? Math.max(1, Math.floor(height / lineHeight)) : lines.length;
      const visibleLines = lines.slice(0, maxLines);

      visibleLines.forEach((line, idx) => {
        const tspan = svg.ownerDocument.createElementNS(svgNs, 'tspan');
        tspan.setAttribute('x', String(textX));
        tspan.setAttribute('dy', idx === 0 ? '0' : String(lineHeight));
        tspan.textContent = line;
        textEl.appendChild(tspan);
      });
    } else {
      textEl.textContent = textContent;
    }

    parent.insertBefore(textEl, fo);
    parent.removeChild(fo);
  }
}

function removeExternalImages(svg: SVGSVGElement): void {
  const images = Array.from(svg.querySelectorAll('image'));
  for (const img of images) {
    const href = img.getAttribute('href') ?? img.getAttribute('xlink:href') ?? '';
    if (/^(https?:)?\/\//i.test(href) && !href.startsWith('data:')) {
      img.remove();
    }
  }
}

function serializeSvg(svg: SVGSVGElement, width: number, height: number, options?: SerializeSvgOptions): string {
  const clone = svg.cloneNode(true) as SVGSVGElement;
  if (!clone.getAttribute('xmlns')) {
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  }
  if (!clone.getAttribute('xmlns:xlink')) {
    clone.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
  }
  clone.setAttribute('width', String(width));
  clone.setAttribute('height', String(height));

  if (options?.stripForeignObjects) {
    // Some browsers taint canvases when SVG contains foreignObject (or any
    // cross-origin resources inside it). For a robust export, flatten nodes to text.
    convertForeignObjectsToText(clone);
    removeExternalImages(clone);
  }

  return new XMLSerializer().serializeToString(clone);
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load SVG for PNG export'));
    img.src = src;
  });
}

async function canvasToBlob(canvas: HTMLCanvasElement, type: string): Promise<Blob> {
  if (canvas.toBlob) {
    return new Promise((resolve, reject) => {
      try {
        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('Failed to export PNG'));
            return;
          }
          resolve(blob);
        }, type);
      } catch (error) {
        reject(error);
      }
    });
  }

  const dataUrl = canvas.toDataURL(type);
  const response = await fetch(dataUrl);
  return response.blob();
}

function isTaintedCanvasError(error: unknown): boolean {
  if (!error) return false;
  if (error instanceof DOMException) {
    return error.name === 'SecurityError' || /tainted/i.test(error.message);
  }
  if (error instanceof Error) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const name = (error as any).name as string | undefined;
    return name === 'SecurityError' || /tainted/i.test(error.message);
  }
  return false;
}

async function renderSvgStringToPngBlob(
  svgString: string,
  svgWidth: number,
  svgHeight: number,
  options?: ExportPngOptions
): Promise<Blob> {
  const scale = options?.scale ?? 2;
  const backgroundColor = options?.backgroundColor ?? '#ffffff';
  const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(svgBlob);

  try {
    const img = await loadImage(url);
    const canvas = document.createElement('canvas');
    const width = svgWidth;
    const height = svgHeight;

    canvas.width = Math.max(1, Math.floor(width * scale));
    canvas.height = Math.max(1, Math.floor(height * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas context unavailable for PNG export');

    ctx.setTransform(scale, 0, 0, scale, 0, 0);
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(img, 0, 0, width, height);

    return await canvasToBlob(canvas, 'image/png');
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function exportSvgToPngBlob(svg: SVGSVGElement, options?: ExportPngOptions): Promise<Blob> {
  const { width, height } = getSvgExportDimensions(svg);
  const svgString = serializeSvg(svg, width, height);
  try {
    return await renderSvgStringToPngBlob(svgString, width, height, options);
  } catch (error) {
    if (!isTaintedCanvasError(error)) throw error;
    const fallbackSvgString = serializeSvg(svg, width, height, { stripForeignObjects: true });
    return await renderSvgStringToPngBlob(fallbackSvgString, width, height, options);
  }
}
